import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ProtectedRoute } from './components/ProtectedRoute'
import { LoadingFallback } from './components/LoadingFallback'
import { EventDispatcher } from './services/ai/dispatcher/EventDispatcher'
import { initializeEventHandlers } from './services/ai/dispatcher/EventHandlers'

// Lazy-loaded page components for code splitting
// This reduces initial bundle size by loading pages on-demand

// Public pages (loaded immediately for fast initial access)
import { Login } from './pages/Login'
import { Register } from './pages/Register'
import { QuickAccess } from './pages/QuickAccess'
import { ResetPassword } from './pages/ResetPassword'
import { UpdatePassword } from './pages/UpdatePassword'

import { logger } from '@/lib/logger'
// Protected pages (lazy-loaded on navigation)
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })))
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })))
const SimulationWizard = lazy(() => import('./pages/SimulationWizard').then(m => ({ default: m.SimulationWizard })))
const EditScenario = lazy(() => import('./pages/EditScenario').then(m => ({ default: m.EditScenario })))
const FacilitatorSimulation = lazy(() => import('./pages/FacilitatorSimulation').then(m => ({ default: m.FacilitatorSimulation })))
const PrintableMaterials = lazy(() => import('./pages/PrintableMaterials').then(m => ({ default: m.PrintableMaterials })))
const WaitingRoom = lazy(() => import('./pages/WaitingRoom').then(m => ({ default: m.WaitingRoom })))
const RoleReveal = lazy(() => import('./pages/RoleReveal').then(m => ({ default: m.RoleReveal })))
const RoleBriefing = lazy(() => import('./pages/RoleBriefing').then(m => ({ default: m.RoleBriefing })))
const ParticipantDashboard = lazy(() => import('./pages/ParticipantDashboard').then(m => ({ default: m.ParticipantDashboard })))
const ParticipantRegistration = lazy(() => import('./pages/ParticipantRegistration').then(m => ({ default: m.ParticipantRegistration })))
const SecretaryInterface = lazy(() => import('./pages/SecretaryInterface').then(m => ({ default: m.SecretaryInterface })))
const ClanSecretaryInterface = lazy(() => import('./pages/ClanSecretaryInterface').then(m => ({ default: m.ClanSecretaryInterface })))

// AI pages (heavy components, definitely lazy-load)
const AILab = lazy(() => import('./pages/AILab').then(m => ({ default: m.AILab })))
const AISystemTest = lazy(() => import('./pages/AISystemTest').then(m => ({ default: m.AISystemTest })))
const AISystemSetup = lazy(() => import('./pages/AISystemSetup').then(m => ({ default: m.AISystemSetup })))
const AIParticipantRoom = lazy(() => import('./pages/AIParticipantRoom').then(m => ({ default: m.AIParticipantRoom })))
const AIParticipantInterface = lazy(() => import('./pages/AIParticipantInterface').then(m => ({ default: m.AIParticipantInterface })))
const PublicScreen = lazy(() => import('./pages/PublicScreen').then(m => ({ default: m.PublicScreen })))
const SimAnalysisPage = lazy(() => import('./pages/SimAnalysisPage').then(m => ({ default: m.SimAnalysisPage })))

function App() {
  // Initialize AI event handlers once on app startup
  useEffect(() => {
    const dispatcher = EventDispatcher.getInstance()
    initializeEventHandlers(dispatcher)
    logger.log('✅ AI Event Handlers Initialized')
  }, [])

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/quick-access" element={<QuickAccess />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/simulation/create"
          element={
            <ProtectedRoute>
              <SimulationWizard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/simulation/edit/:runId"
          element={
            <ProtectedRoute>
              <SimulationWizard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scenario/edit"
          element={
            <ProtectedRoute>
              <EditScenario />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-lab"
          element={
            <ProtectedRoute>
              <AILab />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-system-test"
          element={
            <ProtectedRoute>
              <AISystemTest />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-system-setup"
          element={
            <ProtectedRoute>
              <AISystemSetup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-room/:roleId"
          element={
            <ProtectedRoute>
              <AIParticipantRoom />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-participant/:runId/:roleId"
          element={
            <AIParticipantInterface />
          }
        />
        <Route
          path="/public-screen/:runId"
          element={
            <PublicScreen />
          }
        />
        <Route
          path="/secretary/:runId"
          element={
            <ProtectedRoute>
              <SecretaryInterface />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clan-secretary/:runId/:clanId"
          element={
            <ProtectedRoute>
              <ClanSecretaryInterface />
            </ProtectedRoute>
          }
        />
        <Route
          path="/facilitator/sim-analysis"
          element={
            <ProtectedRoute>
              <SimAnalysisPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/facilitator/simulation/:runId"
          element={
            <ProtectedRoute>
              <FacilitatorSimulation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/facilitator/simulation/:runId/register"
          element={
            <ProtectedRoute>
              <ParticipantRegistration />
            </ProtectedRoute>
          }
        />
        <Route
          path="/simulation/:runId/print"
          element={
            <ProtectedRoute>
              <PrintableMaterials />
            </ProtectedRoute>
          }
        />
        <Route
          path="/waiting-room/:runId"
          element={
            <ProtectedRoute>
              <WaitingRoom />
            </ProtectedRoute>
          }
        />
        <Route
          path="/role-reveal/:runId"
          element={
            <ProtectedRoute>
              <RoleReveal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/role-briefing/:runId"
          element={
            <ProtectedRoute>
              <RoleBriefing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/participant-dashboard"
          element={
            <ProtectedRoute>
              <ParticipantDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/participant-dashboard/:runId"
          element={
            <ProtectedRoute>
              <ParticipantDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/printable-materials/:runId/:roleId"
          element={
            <ProtectedRoute>
              <PrintableMaterials />
            </ProtectedRoute>
          }
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  )
}

export default App
